import { queryOptions } from "@tanstack/solid-query";
import { axiosClient } from "~/js/network/axiosClient";
import { BaseApi } from "./baseApi";

// Types for Tier 1: Personal Credits
export interface CreditsBreakdown {
	credits_left: number;
	credits_received: number;
	credit_type_name: string;
}

export interface CreditsResponse {
	credits_balance: number;
	is_infinity: boolean;
	credits_balance_breakdowns: CreditsBreakdown[];
	expiration_date: string | null;
	count_credits_by_sub: number;
}

// Types for Tier 2: Team Credits
export interface TeamMemberCredits {
	company_account_id: number;
	user_id: string | null;
	user_name: string | null;
	credits_balance: number;
	credits_break_down: CreditsBreakdown[];
	is_infinity: boolean;
}

export interface TeamCreditsInfoResponse {
	members: TeamMemberCredits[];
}

export class CreditsApi extends BaseApi {
	static baseUrl = "api/v2";

	/**
	 * Tier 1: Get personal credits for current user
	 * Lightweight endpoint that returns only credits data without subscription info
	 */
	static getOnlineCreditsOptions() {
		return queryOptions({
			queryKey: ["onlineCredits"],
			queryFn: () => CreditsApi.getOnlineCredits(),
			refetchInterval: 30000, // 30 seconds - less aggressive
			refetchOnWindowFocus: true,
			staleTime: 10000, // 10 seconds
			gcTime: 60000 // 1 minute
		});
	}

	static async getOnlineCredits(): Promise<CreditsResponse> {
		const { data } = await axiosClient.axiosClient.get<CreditsResponse>(
			`/${CreditsApi.baseUrl}/front/online_credits`
		);
		return data;
	}

	/**
	 * Tier 2: Get team credits information
	 * Returns credits info for all team members (owner) or just current user (member)
	 */
	static getTeamCreditsInfoOptions() {
		return queryOptions({
			queryKey: ["teamCreditsInfo"],
			queryFn: () => CreditsApi.getTeamCreditsInfo(),
			refetchInterval: 30000, // 30 seconds - less aggressive
			refetchOnWindowFocus: true,
			staleTime: 10000, // 10 seconds
			gcTime: 60000 // 1 minute
		});
	}

	static async getTeamCreditsInfo(): Promise<TeamCreditsInfoResponse> {
		const { data } = await axiosClient.axiosClient.get<TeamCreditsInfoResponse>(
			`/${CreditsApi.baseUrl}/team/credits_info`
		);
		return data;
	}
}
